import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, ShieldCheck, Award, Star, MapPin, ChevronDown, ChevronLeft, ChevronRight, LayoutGrid, List as ListIcon, Users, ArrowRight, Calendar, CreditCard, TrendingUp } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { IMAGES } from "../mock";
import PublicNavbar from "../components/landing/PublicNavbar";
import PublicInspectorCard from "../components/landing/PublicInspectorCard";
import { Footer } from "../components/landing/Sections";
import { LoginDialog } from "./LandingPage";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BENEFIT_ICONS = [ShieldCheck, Award, Star, MapPin];
const CTA_ICONS = [Users, Calendar, CreditCard, TrendingUp];
const PAGE_SIZE = 12;

function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => { const id = setTimeout(() => setV(value), delay); return () => clearTimeout(id); }, [value, delay]);
  return v;
}

export default function InspectorsPublicPage() {
  const { t } = useLang();
  const [loginOpen, setLoginOpen] = useState(false);
  const [q, setQ] = useState("");
  const [vehicleType, setVehicleType] = useState("all");
  const [city, setCity] = useState("all");
  const [sort, setSort] = useState("rating");
  const [page, setPage] = useState(1);
  const [view, setView] = useState("grid");
  const dq = useDebounced(q);
  const promptLogin = () => setLoginOpen(true);

  useEffect(() => { setPage(1); }, [dq, vehicleType, city, sort]);

  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: () => api.get("/meta").then((r) => r.data), staleTime: Infinity });
  const { data: totalData } = useQuery({ queryKey: ["pub-total"], queryFn: () => api.get("/inspectors/public", { params: { page_size: 1 } }).then((r) => r.data), staleTime: Infinity });
  const { data, isLoading } = useQuery({
    queryKey: ["public-inspectors", dq, vehicleType, city, sort, page],
    queryFn: () => api.get("/inspectors/public", {
      params: { q: dq || undefined, vehicle_type: vehicleType === "all" ? undefined : vehicleType, city, sort, page, page_size: PAGE_SIZE },
    }).then((r) => r.data),
    keepPreviousData: true,
  });

  const totalAll = totalData?.total ?? 50;
  const total = data?.total ?? 0;
  const totalPages = data?.total_pages ?? 1;
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);
  const vehicleTypes = meta?.vehicle_types || [];
  const cities = meta?.cities || [];
  const pages = useMemo(() => Array.from({ length: totalPages }, (_, i) => i + 1), [totalPages]);

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <PublicNavbar onLogin={promptLogin} active="inspector" />

      {/* Hero */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 lg:py-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.1]">
              <span className="text-blue-600">{totalAll}</span> <span className="text-slate-900">{t("inspectors.heroCount")}</span>
            </h1>
            <p className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">{t("inspectors.heroTitle2")}</p>
            <p className="mt-4 text-slate-500 text-sm sm:text-base max-w-lg leading-relaxed">{t("inspectors.heroDesc")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-7 max-w-xl">
              {t("inspectors.benefits").map((b, i) => {
                const Icon = BENEFIT_ICONS[i];
                return (
                  <div key={i} className="flex items-start gap-2">
                    <span className="mt-0.5 text-blue-600 shrink-0"><Icon size={20} fill={i < 2 ? "#dbeafe" : "none"} /></span>
                    <p className="text-[11px] font-bold text-slate-700 leading-tight">{b.title}<br /><span className="font-semibold text-slate-500">{b.sub}</span></p>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="relative h-64 lg:h-[360px] rounded-3xl overflow-hidden">
            <img src={IMAGES.hero} alt="Inspector profesional Inspecar" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
            <p className="absolute top-6 right-5 font-hand text-2xl sm:text-3xl text-white -rotate-3 text-right leading-tight drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)] max-w-[220px]">
              {t("inspectors.hand")}
            </p>
            <div className="absolute bottom-5 left-5 bg-white rounded-2xl shadow-xl p-3 flex items-center gap-3" data-testid="hero-join-badge">
              <span className="bg-blue-600 rounded-full p-2"><Users size={18} className="text-white" /></span>
              <div>
                <p className="text-xs font-bold text-slate-900 leading-tight">{t("inspectors.badgeJoin", { count: totalAll })}</p>
                <p className="text-[11px] text-slate-500 font-medium">{t("inspectors.badgeJoinSub")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter bar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 -mt-6 relative z-10">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-lg p-4 grid gap-3 lg:grid-cols-[1.4fr_1fr_1fr_1fr_auto]">
          <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 h-11">
            <Search size={16} className="text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("inspectors.searchPlaceholder")} data-testid="search-input" className="flex-1 text-sm outline-none bg-transparent" />
          </div>
          <FilterSelect label={t("inspectors.jenisKendaraan")} value={vehicleType} onChange={setVehicleType} testid="vehicle-select"
            options={[{ id: "all", label: t("inspectors.semua") }, ...vehicleTypes.map((v) => ({ id: v.id, label: t(`vehicle.${v.id}`) }))]} />
          <FilterSelect label={t("inspectors.lokasi")} value={city} onChange={setCity} testid="city-select"
            options={[{ id: "all", label: t("inspectors.pilihKota") }, ...cities.map((c) => ({ id: c, label: c }))]} />
          <FilterSelect label={t("inspectors.urutkan")} value={sort} onChange={setSort} testid="sort-select"
            options={["rating", "price_low", "experience", "reviews", "distance"].map((s) => ({ id: s, label: t(`inspectors.sort.${s}`) }))} />
          <button onClick={() => toast.info(t("inspectors.mapSoon"))} data-testid="view-map-btn"
            className="flex items-center justify-center gap-1.5 text-sm font-bold text-blue-600 border border-blue-200 rounded-xl px-4 h-11 self-end hover:bg-blue-50 transition-colors whitespace-nowrap">
            <MapPin size={16} /> {t("inspectors.lihatDiPeta")}
          </button>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
          <div>
            <h2 className="text-xl font-extrabold text-slate-900" data-testid="list-title">{t("inspectors.listTitle")} ({total})</h2>
            <p className="text-sm text-slate-500 mt-0.5">{t("inspectors.subtitle")}</p>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-xs font-semibold text-slate-400" data-testid="showing-count">{t("inspectors.showing", { from, to, total })}</p>
            <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden">
              <button onClick={() => setView("grid")} data-testid="view-grid" className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold ${view === "grid" ? "bg-blue-600 text-white" : "text-slate-500"}`}><LayoutGrid size={14} /> {t("inspectors.grid")}</button>
              <button onClick={() => setView("list")} data-testid="view-list" className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold ${view === "list" ? "bg-blue-600 text-white" : "text-slate-500"}`}><ListIcon size={14} /> {t("inspectors.list")}</button>
            </div>
          </div>
        </div>

        {isLoading && !data ? (
          <div className="py-24 text-center text-slate-400 text-sm" data-testid="loading">Loading…</div>
        ) : total === 0 ? (
          <div className="py-24 text-center" data-testid="empty-state">
            <Search size={40} className="mx-auto text-slate-300" />
            <p className="mt-4 font-bold text-slate-700">{t("inspectors.emptyTitle")}</p>
            <p className="text-sm text-slate-400 mt-1">{t("inspectors.emptyDesc")}</p>
          </div>
        ) : (
          <div className={view === "grid" ? "grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" : "grid gap-4"} data-testid="inspectors-grid">
            {data.items.map((insp) => <PublicInspectorCard key={insp.user_id} insp={insp} onLogin={promptLogin} list={view === "list"} />)}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-1.5 mt-10" data-testid="pagination">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} data-testid="page-prev"
              className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 disabled:opacity-40 hover:border-blue-300 transition-colors"><ChevronLeft size={16} /></button>
            {pages.map((p) => (
              <button key={p} onClick={() => setPage(p)} data-testid={`page-${p}`}
                className={`w-9 h-9 rounded-lg text-sm font-bold transition-colors ${p === page ? "bg-blue-600 text-white" : "border border-slate-200 text-slate-600 hover:border-blue-300"}`}>{p}</button>
            ))}
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} data-testid="page-next"
              className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-500 disabled:opacity-40 hover:border-blue-300 transition-colors"><ChevronRight size={16} /></button>
          </div>
        )}
      </section>

      {/* CTA join */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-12">
        <div className="rounded-3xl bg-gradient-to-r from-blue-50 via-slate-50 to-white border border-blue-100 p-8 sm:p-10 grid lg:grid-cols-[1fr_auto] gap-8 items-center">
          <div>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 leading-tight">{t("inspectors.ctaTitle1")}<br />{t("inspectors.ctaTitle2")}</h3>
            <p className="text-sm text-slate-500 mt-3 max-w-md leading-relaxed">{t("inspectors.ctaDesc")}</p>
            <div className="grid sm:grid-cols-2 gap-3 mt-6 max-w-xl">
              {t("inspectors.ctaPoints").map((p, i) => {
                const Icon = CTA_ICONS[i];
                return <p key={i} className="flex items-center gap-2 text-xs font-semibold text-slate-700"><span className="bg-blue-100 rounded-full p-1.5"><Icon size={13} className="text-blue-600" /></span> {p}</p>;
              })}
            </div>
          </div>
          <Button onClick={promptLogin} data-testid="cta-register-btn" className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl px-7 h-12 shadow-lg shadow-blue-600/25 whitespace-nowrap">
            {t("inspectors.ctaButton")} <ArrowRight size={16} className="ml-1.5" />
          </Button>
        </div>
      </section>

      <Footer />
      <LoginDialog open={loginOpen} setOpen={setLoginOpen} />
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="border border-slate-200 rounded-xl px-3 h-11 flex flex-col justify-center">
      <span className="text-[10px] font-bold text-slate-400 leading-none">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="border-0 shadow-none h-auto p-0 text-sm font-bold text-slate-800 focus:ring-0 [&>svg]:opacity-60" data-testid={testid}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
